import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { SkeletonBlock } from '../../ui/Skeleton';
import './notebook.css';

type Category = 'movie' | 'trailer' | 'tv' | 'music' | 'podcast' | 'video' | 'article' | 'other';
type Intent = 'play' | 'stream' | 'read' | 'open';

interface NotebookItem {
  itemId: string;
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  durationSeconds: number | null;
  provider: string;
  category: Category;
  categoryReason: string;
  categorizedBy: string | null;
  intent: Intent;
  shared: boolean;
  doneAt: number | null;
  addedBy: string;
  createdAt: number;
  updatedAt: number;
}

const CATEGORY_LABEL: Record<Category, string> = {
  movie: 'Movie', trailer: 'Trailer', tv: 'TV', music: 'Music',
  podcast: 'Podcast', video: 'Video', article: 'Article', other: 'Uncategorised',
};

const INTENT_VERB: Record<Intent, string> = {
  play: 'Play', stream: 'Stream', read: 'Read', open: 'Open',
};

const FILTERS: Array<{ key: Category | 'all' | 'todo'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To watch' },
  { key: 'movie', label: 'Movies' },
  { key: 'trailer', label: 'Trailers' },
  { key: 'tv', label: 'TV' },
  { key: 'music', label: 'Music' },
  { key: 'podcast', label: 'Podcasts' },
  { key: 'video', label: 'Video' },
  { key: 'article', label: 'Articles' },
];

function durationLabel(s: number | null): string | null {
  if (!s) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

function hostOf(u: string): string {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; }
}

/**
 * Shared Notebook — paste a link, it becomes a card for both partners
 * (epic szvNTWusHt3f). Metadata unfurls server-side after the card exists,
 * so pasting never blocks; classification is shown with its rationale and
 * can always be overridden by either partner.
 */
export function NotebookPanel() {
  const me = session();
  const [items, setItems] = useState<NotebookItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<Category | 'all' | 'todo'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await api<{ items: NotebookItem[] }>('/api/notebook');
      setItems(data.items ?? []);
      setFailed(false);
    } catch (e) {
      console.error('[Pear] notebook load failed', e);
      setItems([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void reload();
    // Unfurl finishes after the response lands — poll twice so a fresh paste
    // fills in without the partner having to pull-to-refresh.
    const t1 = window.setTimeout(() => { void reload(); }, 2500);
    const t2 = window.setTimeout(() => { void reload(); }, 6000);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [reload]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const url = draft.trim();
    if (!url || adding) return;
    setAdding(true);
    try {
      await api('/api/notebook', { body: { url } });
      setDraft('');
      await reload();
      window.setTimeout(() => { void reload(); }, 2500);
    } catch (err) {
      console.error('[Pear] notebook add failed', err);
    } finally {
      setAdding(false);
    }
  };

  const toggleDone = async (item: NotebookItem) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { done: item.doneAt === null } });
      await reload();
    } catch (e) {
      console.error('[Pear] done toggle failed', e);
    } finally {
      setBusyId(null);
    }
  };

  const retag = async (item: NotebookItem, category: Category) => {
    if (busyId) return;
    const reason = window.prompt(`Why "${CATEGORY_LABEL[category]}"? (shown on the card)`, item.categoryReason || '');
    if (reason === null) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}/category`, { body: { category, reason: reason.trim() } });
      await reload();
    } catch (e) {
      console.error('[Pear] retag failed', e);
      window.alert('That tag needs a reason to save.');
    } finally {
      setBusyId(null);
    }
  };

  const visible = (items ?? []).filter(i => {
    if (filter === 'all') return true;
    if (filter === 'todo') return i.doneAt === null;
    return i.category === filter;
  });

  return (
    <div className="nb-panel">
      <h2 className="nb-title">Notebook</h2>
      <p className="nb-lead">Paste a link — it lands here for both of you, with the title filled in.</p>

      <form className="nb-card nb-composer" onSubmit={add}>
        <label className="nb-label" htmlFor="nb-url">Add a link</label>
        <input
          id="nb-url"
          type="url"
          inputMode="url"
          className="pear-chat-input nb-input"
          placeholder="https://…"
          aria-label="Link to save"
          value={draft}
          onChange={e => setDraft(e.target.value)}
        />
        <div className="nb-composer-foot">
          <span className="nb-hint">Trailers, films, albums, podcasts, articles</span>
          <button type="submit" className="pear-btn pear-btn-mustard nb-add" disabled={adding || draft.trim().length === 0}>
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>

      <div className="nb-filters" role="group" aria-label="Filter by category">
        {FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            aria-pressed={filter === f.key}
            className={`nb-filter${filter === f.key ? ' is-on' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="nb-list">
        {items === null && !failed && <SkeletonBlock lines={3} />}

        {failed && <p className="nb-empty">Couldn't load your notebook.</p>}

        {items !== null && items.length === 0 && !failed && (
          <div className="nb-empty-state">
            <p className="nb-empty-title">Nothing saved yet.</p>
            <p className="nb-empty-hint">Drop a trailer you both want to see, or an album you keep coming back to.</p>
          </div>
        )}

        {items !== null && items.length > 0 && visible.length === 0 && (
          <p className="nb-empty">No cards in this filter yet.</p>
        )}

        {visible.map(item => {
          const done = item.doneAt !== null;
          return (
            <article className={`nb-card nb-item${done ? ' is-done' : ''}`} key={item.itemId}>
              {item.imageUrl && (
                <img className="nb-art" src={item.imageUrl} alt="" loading="lazy" />
              )}
              <div className="nb-body">
                <div className="nb-meta">
                  <span className={`nb-cat nb-cat-${item.category}`}>{CATEGORY_LABEL[item.category]}</span>
                  <span className="nb-provider">{item.provider === 'unknown' ? hostOf(item.url) : item.provider}</span>
                  {durationLabel(item.durationSeconds) && (
                    <span className="nb-duration">{durationLabel(item.durationSeconds)}</span>
                  )}
                </div>
                <h3 className="nb-item-title">{item.title ?? hostOf(item.url)}</h3>
                {item.description && <p className="nb-desc">{item.description}</p>}
                <p className="nb-url">{hostOf(item.url)}</p>

                {item.categoryReason && (
                  <p className="nb-reason" title="Why this card was categorised this way">
                    {item.categoryReason}
                    {item.categorizedBy === null && item.category !== 'other' ? ' · suggested' : ' · yours'}
                  </p>
                )}

                <div className="nb-actions">
                  <a className="nb-intent" href={item.url} target="_blank" rel="noreferrer noopener">
                    {INTENT_VERB[item.intent]} ↗
                  </a>
                  <Pressable
                    className={`nb-done${done ? ' is-on' : ''}`}
                    disabled={busyId === item.itemId}
                    onClick={() => void toggleDone(item)}
                    aria-pressed={done}
                  >
                    {done ? '✓ Watched' : 'Mark watched'}
                  </Pressable>
                  <select
                    className="nb-retag"
                    aria-label="Change category"
                    value={item.category}
                    disabled={busyId === item.itemId}
                    onChange={e => void retag(item, e.target.value as Category)}
                  >
                    {(Object.keys(CATEGORY_LABEL) as Category[]).map(c => (
                      <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {me === null && <p className="nb-empty">Log in to see the shared notebook.</p>}
    </div>
  );
}